'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import Link from 'next/link';
import { getStoreCategories } from '@/lib/thegame/store-client';

/**
 * Category Showcase Component
 * Redesigned as a Typographic Pill Cloud for a sleek, modern look.
 * Optimized with Server-Side Aggregation (BFF) to protect user bandwidth.
 */
interface CategoryShowcaseProps {
  title?: string;
  subtitle?: string;
}

interface Category {
  id: string;
  name: string;
  count: number;
}

export default function CategoryShowcase({
  title = 'Explore Categories',
  subtitle = '',
}: CategoryShowcaseProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        setLoading(true);
        const { success, categories: fetchedCategories } = await getStoreCategories();
        if (cancelled) return;
        
        if (success && Array.isArray(fetchedCategories)) {
          setCategories(fetchedCategories);
        }
      } catch (error) {
        if (!cancelled) console.error('Failed to load categories:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-card">
        <div className="max-w-7xl mx-auto text-center">
          <div className="h-10 w-64 bg-muted animate-pulse mx-auto rounded-lg mb-12"></div>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="h-12 w-32 bg-muted animate-pulse rounded-full"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) return null;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-card">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-4">
              <Sparkles className="w-10 h-10 mx-auto text-primary opacity-50" />
            </div>
            <h2 className="text-5xl font-black tracking-tighter text-foreground">{title}</h2>
          </motion.div>
        </div>

        {/* Pill Cloud Layout */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto"
        >
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                href={`/store?category=${category.id}`}
                className="group flex items-center gap-3 px-6 py-3 rounded-full border border-border bg-card text-foreground font-medium transition-all duration-300 hover:border-cyan-500 hover:text-cyan-500 hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] whitespace-nowrap"
              >
                <span>{category.name}</span>
                <span className="text-xs text-muted-foreground group-hover:text-cyan-500/70 transition-colors">
                  {category.count}
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}